import {
    normalizeLossesVendor,
    resolveVendorLossesKey,
    vendorLossesValues,
    type ActiveLossesVendor,
} from '../../Constants';
import { isEnfinLossesSource } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/designLossesFinanceGate';
import { isEnfin } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/productsFinanceHubHelpers';
import { useOneButtonStore } from '../../store/oneButtonStore';
import { useViewerStore } from '../../store/store';
import type { PreContractDesignPartnerRecommendation } from './preContractProductPartners';
import { applyPciLossesVendor, postPciLossesVendorToEmbed } from './preContractEmbedBridge';

export function resolveLossesVendorForPartner(
    partner: string | null | undefined,
): ActiveLossesVendor {
    return isEnfin(String(partner ?? '')) ? 'EnFin' : 'oneDRAW';
}

/**
 * The vendor the design is actually on. Callers that subscribe to the stores must resolve
 * it through here too — reading only the viewer store made the UI show oneDRAW while
 * `applyLossesVendor` already considered the design to be on EnFin, so switching no-opped.
 */
export function resolveActiveLossesVendor(
    viewerVendor: string | null | undefined,
    systemDesignLosses: unknown,
): ActiveLossesVendor {
    // An explicit pick in the viewer wins in both directions. The saved design's losses
    // only seed the answer before anything has been chosen, otherwise a design stored as
    // Enfin could never be switched back to oneDRAW.
    const picked = String(viewerVendor ?? '').trim();
    if (picked) return normalizeLossesVendor(picked);
    return isEnfinLossesSource(systemDesignLosses) ? 'EnFin' : 'oneDRAW';
}

export function getCurrentLossesVendor(): ActiveLossesVendor {
    return resolveActiveLossesVendor(
        useViewerStore.getState().selectedLossesVendor,
        useOneButtonStore.getState().systemDesignLosses,
    );
}

/**
 * Retention after the flat loss rows for a vendor. Rows carrying a formula ("Calculation",
 * "Varies.") or a range are identical across vendors, so skipping them leaves the ratio intact.
 */
function lossesVendorRetention(vendor: ActiveLossesVendor): number {
    const rows = vendorLossesValues[resolveVendorLossesKey(vendor)] ?? [];
    return rows.reduce((retention, row) => {
        const percent = Number(row.percentage);
        if (!Number.isFinite(percent) || percent <= 0) return retention;
        return retention * (1 - percent / 100);
    }, 1);
}

/** Production scale when the same design is priced under a different losses vendor. */
export function lossesVendorProductionRatio(
    from: ActiveLossesVendor,
    to: ActiveLossesVendor,
): number {
    if (from === to) return 1;
    const fromRetention = lossesVendorRetention(from);
    if (!(fromRetention > 0)) return 1;
    return lossesVendorRetention(to) / fromRetention;
}

export function applyLossesVendor(
    targetVendor: ActiveLossesVendor,
    options?: { force?: boolean },
): boolean {
    const currentVendor = getCurrentLossesVendor();
    const force = options?.force === true;
    const state = useViewerStore.getState();
    const solvedVendor = state.pciProductionLossesVendor
        ? normalizeLossesVendor(state.pciProductionLossesVendor)
        : currentVendor;

    // Same Confirm path as LossesVendorSection: store vendor, multipliers, RefreshShade.
    const viewer = state.sceneViewer;
    if (viewer) {
        applyPciLossesVendor(viewer, targetVendor, { force });
    }
    postPciLossesVendorToEmbed(targetVendor, { force });
    useViewerStore.setState({
        selectedLossesVendor: targetVendor,
        // Keep attributing live kWh to the last solved vendor until shade finishes.
        pciProductionLossesVendor: solvedVendor,
        pciProductionLossesPending: targetVendor,
    });
    return force || currentVendor !== targetVendor;
}

function isAssignedOpportunityPartner(partner: string | null | undefined): boolean {
    const label = String(partner ?? '').trim();
    return label.length > 0 && label.toLowerCase() !== 'select finance partner';
}

export function resolveSolarFinancePartner(options: {
    designPartner?: PreContractDesignPartnerRecommendation | null;
    opportunityPartner?: string | null;
    suggestedPartner?: string | null;
}): string | null {
    const fromDesign = options.designPartner?.partner?.trim();
    if (fromDesign) return fromDesign;

    if (isAssignedOpportunityPartner(options.opportunityPartner)) {
        return String(options.opportunityPartner).trim();
    }

    const suggested = options.suggestedPartner?.trim();
    return suggested || null;
}

export function readOpportunitySolarPartner(opportunityDetails: unknown): string | null {
    const products = (opportunityDetails as { OpportunityProduct?: unknown[] } | null)
        ?.OpportunityProduct;
    if (!Array.isArray(products)) return null;

    const solar = products.find(
        (product) =>
            String((product as { Product_Type_Name?: string }).Product_Type_Name ?? '') ===
                'Solar' &&
            (product as { ActiveFlag?: boolean })?.ActiveFlag !== false,
    );
    if (!solar) return null;

    return String(
        (solar as { Finance_Partner_Display_Name?: string }).Finance_Partner_Display_Name ?? '',
    ).trim() || null;
}
